import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ClientHeader from "../../components/Client/ClientHeader";
import { getProductById } from "../../api/productService";
import { getStoreById } from "../../api/storeService";
import { getProductReviews, getProductReviewSummary } from "../../api/reviewService";
import { startStoreConversation } from "../../api/chatService";
import { useWishlist } from "../../context/WishlistContext";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { pickLocalizedName } from "../../utils/localizedName";
import "./ProductDetails.css";

function StarIcon({ fill = "full" }) {
  if (fill === "empty") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M12 3.5l2.7 5.8 6.3.6-4.7 4.3 1.3 6.3L12 17.4l-5.6 3.1 1.3-6.3-4.7-4.3 6.3-.6L12 3.5z" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      {fill === "half" && (
        <defs>
          <linearGradient id="star-half-gradient">
            <stop offset="50%" stopColor="currentColor" />
            <stop offset="50%" stopColor="transparent" />
          </linearGradient>
        </defs>
      )}
      <path
        fill={fill === "half" ? "url(#star-half-gradient)" : "currentColor"}
        stroke="currentColor"
        strokeWidth="1.6"
        d="M12 3.5l2.7 5.8 6.3.6-4.7 4.3 1.3 6.3L12 17.4l-5.6 3.1 1.3-6.3-4.7-4.3 6.3-.6L12 3.5z"
      />
    </svg>
  );
}

function StarRow({ rating = 0 }) {
  const stars = [1, 2, 3, 4, 5].map((position) => {
    if (rating >= position) return "full";
    if (rating >= position - 0.5) return "half";
    return "empty";
  });
  return (
    <span className="product-star-row">
      {stars.map((fill, index) => (
        <StarIcon key={index} fill={fill} />
      ))}
    </span>
  );
}

function StoreIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l1.5-5h15L21 9" />
      <path d="M3 9a2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0 2 2 0 0 0 4 0" />
      <path d="M5 9v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9" />
      <path d="M9 21v-6h6v6" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.6" y1="10.6" x2="15.4" y2="6.4" />
      <line x1="8.6" y1="13.4" x2="15.4" y2="17.6" />
    </svg>
  );
}

function HeartIcon({ filled }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />
    </svg>
  );
}

function formatReviewDate(value) {
  return new Date(value).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function CloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ChevronIcon({ direction = "left" }) {
  const points = direction === "left" ? "15 18 9 12 15 6" : "9 18 15 12 9 6";
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points={points} />
    </svg>
  );
}

function ImageZoomModal({ images, activeIndex, altText, onClose, onNavigate }) {
  const [zoomOrigin, setZoomOrigin] = useState("50% 50%");
  const [isZooming, setIsZooming] = useState(false);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
      else if (event.key === "ArrowLeft") onNavigate(-1);
      else if (event.key === "ArrowRight") onNavigate(1);
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, onNavigate]);

  function handleMouseMove(event) {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    setZoomOrigin(`${x}% ${y}%`);
  }

  const current = images[activeIndex];

  return (
    <div className="image-zoom-modal-overlay" onClick={onClose}>
      <button type="button" className="image-zoom-modal-close" onClick={onClose} aria-label="Close">
        <CloseIcon />
      </button>

      {images.length > 1 && (
        <button
          type="button"
          className="image-zoom-modal-nav image-zoom-modal-nav-prev"
          onClick={(event) => {
            event.stopPropagation();
            onNavigate(-1);
          }}
          aria-label="Previous image"
        >
          <ChevronIcon direction="left" />
        </button>
      )}

      <div
        className="image-zoom-modal-stage"
        onClick={(event) => event.stopPropagation()}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsZooming(true)}
        onMouseLeave={() => setIsZooming(false)}
      >
        <img
          src={current?.imageUrl}
          alt={altText}
          className={`image-zoom-modal-image${isZooming ? " zoomed" : ""}`}
          style={{ transformOrigin: zoomOrigin }}
        />
      </div>

      {images.length > 1 && (
        <button
          type="button"
          className="image-zoom-modal-nav image-zoom-modal-nav-next"
          onClick={(event) => {
            event.stopPropagation();
            onNavigate(1);
          }}
          aria-label="Next image"
        >
          <ChevronIcon direction="right" />
        </button>
      )}

      {images.length > 1 && (
        <div className="image-zoom-modal-thumbnails" onClick={(event) => event.stopPropagation()}>
          {images.map((img, index) => (
            <img
              key={img.imageId ?? index}
              src={img.imageUrl}
              alt=""
              className={`image-zoom-modal-thumbnail${index === activeIndex ? " active" : ""}`}
              onClick={() => onNavigate(index - activeIndex)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductDetails() {
  const { t } = useTranslation(["productDetails", "common"]);
  const { language } = useLanguage();
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, openLoginModal } = useAuth();
  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsTotalCount, setReviewsTotalCount] = useState(0);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [startingChat, setStartingChat] = useState(false);
  const [shareFeedback, setShareFeedback] = useState("");
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const { isWishlisted, toggleWishlist } = useWishlist();
  const { addItem } = useCart();

  const REVIEWS_PAGE_SIZE = 5;

  useEffect(() => {
    setProduct(null);
    setStore(null);
    setNotFound(false);
    setSelectedVariantId(null);
    setReviewSummary(null);
    setReviews([]);
    setReviewsPage(1);
    setReviewsTotalCount(0);

    getProductById(id, "web")
      .then((data) => {
        setProduct(data);
        const main = data.images?.find((img) => img.isMain) || data.images?.[0];
        setActiveImage(main?.imageUrl || null);
        getStoreById(data.storeId)
          .then(setStore)
          .catch(() => setStore(null));
      })
      .catch(() => setNotFound(true));

    getProductReviewSummary(id)
      .then(setReviewSummary)
      .catch(() => {});
    setReviewsLoading(true);
    getProductReviews(id, 1, REVIEWS_PAGE_SIZE)
      .then((data) => {
        setReviews(data.items);
        setReviewsTotalCount(data.totalCount);
      })
      .catch(() => {})
      .finally(() => setReviewsLoading(false));
  }, [id]);

  function loadMoreReviews() {
    const nextPage = reviewsPage + 1;
    setReviewsLoading(true);
    getProductReviews(id, nextPage, REVIEWS_PAGE_SIZE)
      .then((data) => {
        setReviews((prev) => [...prev, ...data.items]);
        setReviewsTotalCount(data.totalCount);
        setReviewsPage(nextPage);
      })
      .catch(() => {})
      .finally(() => setReviewsLoading(false));
  }

  function openGallery(imageUrl) {
    const index = product.images?.findIndex((img) => img.imageUrl === imageUrl);
    setGalleryIndex(index >= 0 ? index : 0);
    setGalleryOpen(true);
  }

  function closeGallery() {
    const img = product.images?.[galleryIndex];
    if (img) setActiveImage(img.imageUrl);
    setGalleryOpen(false);
  }

  function navigateGallery(delta) {
    const total = product.images?.length ?? 0;
    if (total === 0) return;
    setGalleryIndex((prev) => ((prev + delta) % total + total) % total);
  }

  async function handleChat() {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    if (startingChat || !product) return;
    setStartingChat(true);
    try {
      const conversation = await startStoreConversation(product.storeId);
      navigate(`/messages?conversationId=${conversation.conversationId}`);
    } catch {
      // ignore, user can retry
    } finally {
      setStartingChat(false);
    }
  }

  async function handleShare() {
    const url = window.location.href;
    const shareData = { title: product?.name, url };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // user cancelled share sheet
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareFeedback(t("linkCopied"));
      setTimeout(() => setShareFeedback(""), 2000);
    } catch {
      // clipboard unavailable
    }
  }

  if (notFound) {
    return (
      <div className="client-product-details-page">
        <ClientHeader />
        <p className="client-products-status">{t("productNotFound")}</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="client-product-details-page">
        <ClientHeader />
        <p className="client-products-status">{t("common:loading")}</p>
      </div>
    );
  }

  const selectedVariant = product.variants?.find((v) => v.variantId === selectedVariantId);
  const price = selectedVariant?.price ?? product.price;
  const discountPrice = selectedVariant ? selectedVariant.discountPrice : product.discountPrice;
  const hasDiscount = discountPrice != null && discountPrice < price;
  const stock = selectedVariant?.stockQuantity ?? product.stockQuantity;
  const requiresVariantSelection = product.variants?.length > 0 && !selectedVariantId;
  const wishlisted = isWishlisted(product.productId);

  async function handleAddToCart() {
    if (requiresVariantSelection || stock <= 0) return;
    setAddingToCart(true);
    try {
      await addItem(product.productId, { variantId: selectedVariantId ?? undefined });
    } finally {
      setAddingToCart(false);
    }
  }

  return (
    <div className="client-product-details-page">
      <ClientHeader />
      <div className="product-details-content">
        <div className="product-details-gallery">
          {activeImage ? (
            <img
              src={activeImage}
              alt={product.name}
              className="product-details-main-image"
              onClick={() => openGallery(activeImage)}
            />
          ) : (
            <div className="product-details-main-image product-details-image-placeholder" />
          )}

          {product.images?.length > 1 && (
            <div className="product-details-thumbnails">
              {product.images.map((img) => (
                <img
                  key={img.imageId}
                  src={img.imageUrl}
                  alt={product.name}
                  className={`product-details-thumbnail${
                    img.imageUrl === activeImage ? " product-details-thumbnail-active" : ""
                  }`}
                  onClick={() => setActiveImage(img.imageUrl)}
                />
              ))}
            </div>
          )}
        </div>

        {galleryOpen && product.images?.length > 0 && (
          <ImageZoomModal
            images={product.images}
            activeIndex={galleryIndex}
            altText={product.name}
            onClose={closeGallery}
            onNavigate={navigateGallery}
          />
        )}

        <div className="product-details-info">
          <h1 className="product-details-name">{product.name}</h1>

          {pickLocalizedName(language, product.authenticityValue, product.authenticityValueArabic, product.authenticityValueHebrew) && (
            <span className="product-details-authenticity-badge">
              {pickLocalizedName(language, product.authenticityValue, product.authenticityValueArabic, product.authenticityValueHebrew)}
            </span>
          )}

          {product.requiresSpecialDelivery && (
            <span className="product-details-special-delivery-badge">
              {t("specialDeliveryLabel")}
            </span>
          )}

          {store && (
            <div className="product-details-store-row">
              <Link to={`/products?storeId=${product.storeId}`} className="product-details-store-identity">
                {store.logoUrl ? (
                  <img src={store.logoUrl} alt="" className="product-details-store-logo" />
                ) : (
                  <div className="product-details-store-logo product-details-store-logo-placeholder">
                    {store.storeName.charAt(0)}
                  </div>
                )}
                <span className="product-details-store-name">{store.storeName}</span>
              </Link>
              <button
                type="button"
                className="product-details-visit-store-button"
                onClick={() => navigate(`/products?storeId=${product.storeId}`)}
              >
                <StoreIcon />
                {t("visitStore")}
              </button>
            </div>
          )}

          <div className="product-details-meta-row">
            <span className="product-details-rating">
              <StarRow rating={reviewSummary?.averageRating ?? product.rating ?? 0} />
              <span className="product-details-rating-value">
                {Number(reviewSummary?.averageRating ?? product.rating ?? 0).toFixed(1)}
              </span>
              {reviewsTotalCount > 0 && (
                <span className="product-details-rating-count">({reviewsTotalCount})</span>
              )}
            </span>
            {product.soldCount > 0 && (
              <span className="product-details-sold">{t("soldCount", { count: product.soldCount })}</span>
            )}
          </div>

          <div className="product-details-price-row">
            {hasDiscount ? (
              <>
                <span className="product-details-price-discount">{discountPrice.toFixed(2)} ₪</span>
                <span className="product-details-price-original">{price.toFixed(2)} ₪</span>
              </>
            ) : (
              <span className="product-details-price">{price.toFixed(2)} ₪</span>
            )}
          </div>

          <div className="product-details-stock">{stock > 0 ? t("inStockCount", { count: stock }) : t("common:outOfStock")}</div>

          {product.variants?.length > 0 && (
            <div className="product-details-variants">
              <div className="product-details-variants-label">{t("options")}</div>
              <div className="product-details-variants-list">
                {product.variants.map((variant) => (
                  <button
                    key={variant.variantId}
                    type="button"
                    className={`product-details-variant-button${
                      variant.variantId === selectedVariantId ? " product-details-variant-button-active" : ""
                    }`}
                    onClick={() => setSelectedVariantId(variant.variantId)}
                  >
                    {variant.variantName}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="product-details-actions">
            <button
              type="button"
              className={`product-details-icon-button${wishlisted ? " active" : ""}`}
              aria-label={wishlisted ? t("removeFromWishlist") : t("addToWishlist")}
              onClick={() => toggleWishlist(product.productId)}
            >
              <HeartIcon filled={wishlisted} />
            </button>
            <button
              type="button"
              className="product-details-icon-button"
              aria-label={t("chatWithStore")}
              onClick={handleChat}
              disabled={startingChat}
            >
              <ChatIcon />
            </button>
            <div className="product-details-share-wrap">
              <button
                type="button"
                className="product-details-icon-button"
                aria-label={t("shareProduct")}
                onClick={handleShare}
              >
                <ShareIcon />
              </button>
              {shareFeedback && <span className="product-details-share-toast">{shareFeedback}</span>}
            </div>
            <button
              type="button"
              className="product-details-cart-button"
              disabled={stock <= 0 || requiresVariantSelection || addingToCart}
              onClick={handleAddToCart}
            >
              {stock <= 0
                ? t("common:outOfStock")
                : requiresVariantSelection
                  ? t("selectAnOption")
                  : addingToCart
                    ? t("addingToCart")
                    : t("common:addToCart")}
            </button>
          </div>

          {product.description && <p className="product-details-description">{product.description}</p>}

          {product.attributeValues?.length > 0 && (
            <ul className="product-details-attributes">
              {product.attributeValues.map((attributeValue) => (
                <li key={attributeValue.attributeId}>
                  <strong>{attributeValue.attributeName}:</strong>{" "}
                  {attributeValue.optionValue ?? attributeValue.textValue}
                  {attributeValue.unit ? ` ${attributeValue.unit}` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="product-details-reviews">
        <h2 className="product-details-reviews-title">{t("ratingsAndReviews")}</h2>

        <div className="product-details-reviews-summary">
          <div className="product-details-reviews-summary-score">
            {Number(reviewSummary?.averageRating ?? 0).toFixed(1)}
          </div>
          <div className="product-details-reviews-summary-info">
            <StarRow rating={reviewSummary?.averageRating ?? 0} />
            <span className="product-details-reviews-summary-count">
              {t("basedOnReviews", { count: reviewSummary?.totalCount ?? 0 })}
            </span>
          </div>
          {reviewSummary?.totalCount > 0 && (
            <div className="product-details-reviews-breakdown">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = reviewSummary.ratingBreakdown?.[star] ?? 0;
                const percent = reviewSummary.totalCount > 0 ? (count / reviewSummary.totalCount) * 100 : 0;
                return (
                  <div key={star} className="product-details-reviews-breakdown-row">
                    <span className="product-details-reviews-breakdown-label">{star}★</span>
                    <div className="product-details-reviews-breakdown-track">
                      <div className="product-details-reviews-breakdown-fill" style={{ width: `${percent}%` }} />
                    </div>
                    <span className="product-details-reviews-breakdown-count">{count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {reviews.length === 0 && !reviewsLoading ? (
          <p className="product-details-no-reviews">{t("noReviewsYet")}</p>
        ) : (
          <div className="product-details-reviews-list">
            {reviews.map((review) => (
              <div key={review.reviewId} className="product-details-review-card">
                <div className="product-details-review-avatar">
                  {review.userAvatarUrl ? (
                    <img src={review.userAvatarUrl} alt="" />
                  ) : (
                    <span>{review.userName?.charAt(0)?.toUpperCase() ?? "?"}</span>
                  )}
                </div>
                <div className="product-details-review-body">
                  <div className="product-details-review-header">
                    <span className="product-details-review-name">{review.userName}</span>
                    {review.isVerifiedPurchase && (
                      <span className="product-details-review-verified">✓ {t("verifiedPurchase")}</span>
                    )}
                  </div>
                  <div className="product-details-review-meta">
                    <StarRow rating={review.rating} />
                    <span className="product-details-review-date">{formatReviewDate(review.createdAt)}</span>
                  </div>
                  {review.comment && <p className="product-details-review-comment">{review.comment}</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        {reviews.length < reviewsTotalCount && (
          <button
            type="button"
            className="product-details-load-more-reviews"
            onClick={loadMoreReviews}
            disabled={reviewsLoading}
          >
            {reviewsLoading ? t("common:loading") : t("loadMoreReviews")}
          </button>
        )}
      </div>
    </div>
  );
}
